import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthProvider'
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { getDevSession } from '@/lib/dev-login'

/** Checks if current user is a platform admin */
export function RequirePlatformAdmin({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const location = useLocation()
  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) {
      setLoading(false)
      setIsPlatformAdmin(false)
      return
    }

    // Dev login bypass — skip Supabase query when using fake dev token
    const devSession = getDevSession()
    if (devSession && devSession.user.role === 'platform_admin') {
      setIsPlatformAdmin(true)
      setLoading(false)
      return
    }

    const check = async () => {
      try {
        const { data, error } = await supabase
          .from('platform_admins')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle()

        setIsPlatformAdmin(!error && !!data)
      } catch {
        setIsPlatformAdmin(false)
      } finally {
        setLoading(false)
      }
    }

    check()
  }, [user])

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent border-white/30" />
      </div>
    )
  }

  if (!isPlatformAdmin) {
    return <Navigate to="/platform/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}

